import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { DrawerData } from './Drawer';
import {
  createTicketData,
  listTickets,
  getTicket,
  listTicketsByAsset,
  listTicketsByPA,
  listTicketsByRPMAcct,
  listTicketsByCustomer,
  putTicket,
  deleteTicket
} from '../Api/Ticket/TicketApi';
import { addError } from './Error';
import { ApiAdvancedFilter, PaginatedResponse } from './GeneralStore';
import { User, isData, isObserver } from './User';
import {
  associateTicketsByAsset,
  associateTicketsByPA,
  associateTicketsByRPMAcct,
  removeRefByAssetId,
  removeRefByPAId,
  removeRefByRpmAcctId,
  createContractReferences
} from '../Api/Ticket/TicketAssociationApi';
import { CallResult } from '../Api/APIService';
import { ValidatorResult } from '../components/Compounds/CardWithTitle';

export enum TicketType {
  SOURCE = 'Source',
  ASSETS_AND_RPM_ACCOUNTS = 'Assets and RPM Accounts'
}

export enum TicketInquiryType {
  ACCOUNT_DROPPED_OFF = 'Account Dropped Off',
  ORDER_TRACKING_INQUIRY = 'Order Tracking Inquiry',
  ACCOUNT_BILLING_DECREASED = 'Account Billing Decreased',
  CHARGEBACK_QUESTION = 'Chargeback Question',
  BILLING_QUESTION = 'Billing Question',
  INFORMATION_REQUEST = 'Information Request',
  COMMISSION_PERCENT = 'Commission Percent',
  SPIFF_MISSING = 'SPIFF Missing',
  PAYMENT_QUESTION = 'Payment Question',
  REPORTING_QUESTION = 'Reporting Question'
}

export enum TicketResolutionType {
  NONE = 'none',
  DISCONNECT = 'Disconnect',
  INFORMATION_PROVIDED = 'Information Provided',
  TRUE_UP = 'True-Up',
  NO_CORRECTION_NECESSARY = 'No Correction Necessary',
  ACCOUNT_NUMBER_CHANGE = 'Account Number Change',
  MONITORING = "Monitoring - Comp Rec'd as Expected",
  CUSTOMER_CREDIT = 'Customer Credit',
  COMPRESSION = 'Compression',
  DELAYED_INSTALL = 'Delayed Install',
  DENIED_DEAL_NOT_PROPERLY_REGISTERED = 'Denied - Deal Not Properly Registered',
  DENIED_OUTSIDE_OF_SUBMISSION_WINDOW = 'Denied - Outside of Submission Window'
}

export enum TicketStatus {
  NEW = 'New',
  PENDING_PARTNER_RESPONSE = 'Pending Partner Response',
  MONITORING = 'Monitoring',
  PENDING_SOURCE_RESPONSE = 'Pending Source Response',
  PENDING_RECEIPT_OF_SUPPLIER_MONEY = 'Pending Receipt of Supplier $',
  PENDING_ADVISOR_CALL = 'Pending Advisor Call',
  RESOLVED = 'Resolved',
  CLOSED_NO_RESPONSE = 'Closed - No Response',
  DUPLICATE = 'Duplicate'
}

export interface Ticket {
  id: string;
  number: string | null;
  status: TicketStatus;
  inquiryType: TicketInquiryType;
  ticketType: TicketType;
  assignedTo: User;
  proactive: boolean;
  description: string | null;
  receivedDate: string | null;
  submittedToSourceDate: string | null;
  expectedResolutionDate: string | null;
  actualResolutionDate: string | null;
  resolutionType: TicketResolutionType | null;
  resolutionFromSource: string | null;
  supplier: string;
  supplierName: string;
  tsd: string;
  tsdName: string;
  sourceTrackingNumber: string | null;
  firstRun: string | null;
  lastRun: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: User;
  statusDate: string | null;
  customerList: { id: string; sf_id: string; name: string }[] | null;
  apList: { id: string; sf_id: string; name: string }[] | null;
  latestComment: string | null;
  latestCommentAt: string | null;
  latestCommentBy: string | null;
}

export interface AssociationBody {
  [key: string]: string | undefined;
  ticketId?: string;
}

export interface Association {
  type: 'asset' | 'customer' | 'rmpAccount' | 'provisional_asset';
  body?: AssociationBody;
}

const ticketSubject = new BehaviorSubject<Ticket>({} as Ticket);
const ticketDetailSubject = new BehaviorSubject<DrawerData | undefined>(undefined);
const tallyTicketsSubject = new BehaviorSubject<PaginatedResponse<Ticket>>({} as PaginatedResponse<Ticket>);
const ticketLoadingSubject = new BehaviorSubject<boolean>(false);
const tallyTicketsLoadingSubject = new BehaviorSubject<boolean>(false);

const clearError = () => addError('ticket', undefined);

const inboundFetchNumberSubject = new BehaviorSubject<number>(0);
export const getTicketSubject = (): BehaviorSubject<Ticket> => ticketSubject;
export const getTicketDetailSubject = (): BehaviorSubject<DrawerData | undefined> => ticketDetailSubject;
export const getTallyTicketsSubject = (): BehaviorSubject<PaginatedResponse<Ticket>> => tallyTicketsSubject;
export const getTicketLoadingSubject = (): BehaviorSubject<boolean> => ticketLoadingSubject;
export const getTallyTicketsLoadingSubject = (): BehaviorSubject<boolean> => tallyTicketsLoadingSubject;

export const getTicketById = async (id: string) => {
  const cust = tallyTicketsSubject?.getValue()?.data?.find((t) => t?.id === id);
  if (cust) {
    ticketSubject.next(cust);
    return;
  }

  if (!cust) {
    return await fetchTicket(id);
  }
};

export const getTicketDetail = async (id: string): Promise<Ticket | undefined> => {
  const { data, error } = await getTicket(id);
  if (data) clearError();
  if (error) addError('ticket', error.message);
  return data ?? undefined;
};

export const updateTicketDetail = (ticket: DrawerData) => ticketDetailSubject.next(ticket);

export const deleteTicketById = async (id: string, forDrawer?: boolean) => {
  const { data, error } = await deleteTicket(id);
  if (error) {
    addError('ticket', error.message);
  }
  if (data) {
    clearError();
    const subject = forDrawer ? ticketDetailSubject : ticketSubject;
    subject.next({} as Ticket);
    return data;
  }
};

export const fetchTicket = async (id: string) => {
  ticketLoadingSubject.next(true);

  const { data, error } = await getTicket(id);

  if (data) {
    clearError();
    ticketSubject.next(data);
  }
  if (error) {
    addError('ticket', error.message);
    const statusCode = error.errorResponse?.status || 500;
    return statusCode;
  }

  ticketLoadingSubject.next(false);
};

export const fetchTickets = async (filters?: ApiAdvancedFilter, forceRefresh = false) => {
  const newFetchNumber = inboundFetchNumberSubject.getValue() + 1;
  inboundFetchNumberSubject.next(newFetchNumber);
  if (tallyTicketsSubject.getValue()?.data?.length > 0 && !forceRefresh) return;
  tallyTicketsLoadingSubject.next(true);

  const { data, error } = await listTickets(filters);

  const currentFetchNumber = inboundFetchNumberSubject.getValue();
  if (data && newFetchNumber === currentFetchNumber) {
    clearError();
    tallyTicketsSubject.next(data);
  }
  if (error) addError('ticket', error.message);

  newFetchNumber === currentFetchNumber && tallyTicketsLoadingSubject.next(false);
};

export const fetchTicketsByAssetId = async (id: string) => {
  const { data, error } = await listTicketsByAsset(id);

  if (data) {
    clearError();
    return data;
  }
  if (error) addError('ticket', error.message);
};

export const fetchTicketsByPAId = async (id: string) => {
  const { data, error } = await listTicketsByPA(id);

  if (data) {
    clearError();
    return data;
  }
  if (error) addError('ticket', error.message);
};

export const fetchTicketsByRpmAcctId = async (id: string) => {
  const { data, error } = await listTicketsByRPMAcct(id);

  if (data) {
    clearError();
    return data;
  }
  if (error) addError('ticket', error.message);
};

export const fetchTicketsByCustId = async (id: string) => {
  const { data, error } = await listTicketsByCustomer(id);

  if (data) {
    clearError();
    return data;
  }
  if (error) addError('ticket', error.message);
};

const associationRequest = async (
  postFunction: (body: AssociationBody) => Promise<CallResult<unknown>>,
  association: Association
) => {
  if (postFunction && association?.body) {
    const { data, error } = await postFunction(association?.body);

    if (data) clearError();
    if (error) addError('ticket', error.message);
  }
};

export const createAssociations = async (associations: Association[], ticketId?: string) => {
  const promises: Promise<unknown>[] = [];

  associations.forEach((association: Association) => {
    let postFunction;
    switch (association.type) {
      case 'asset': {
        postFunction = associateTicketsByAsset;
        break;
      }
      case 'provisional_asset': {
        postFunction = associateTicketsByPA;
        break;
      }
      case 'rmpAccount': {
        postFunction = associateTicketsByRPMAcct;
        break;
      }
      default:
        break;
    }

    if (postFunction && association?.body && (ticketId || association?.body.ticketId)) {
      if (ticketId) association.body.ticketId = ticketId;
      promises.push(associationRequest(postFunction, association));
    }
  });

  return Promise.all(promises);
};

export const removeAssociation = async (
  objectId: string,
  ticketId: string,
  type: 'asset' | 'provisional_asset' | 'rmpAccount'
) => {
  let deleteFunc;
  switch (type) {
    case 'asset': {
      deleteFunc = removeRefByAssetId;
      break;
    }
    case 'provisional_asset': {
      deleteFunc = removeRefByPAId;
      break;
    }
    case 'rmpAccount': {
      deleteFunc = removeRefByRpmAcctId;
      break;
    }
    default:
      break;
  }

  if (deleteFunc && objectId && ticketId) {
    const { data, error } = await deleteFunc(objectId, ticketId);

    if (data) clearError();
    if (error) addError('ticket', error.message);
  }
};

export const createTicket = async (ticket: Partial<Ticket>, associations?: Association[]) => {
  const ticketData: Ticket = { ...({} as Ticket), ...ticket };

  const { data, error } = await createTicketData(ticketData);

  if (data) {
    clearError();
    if (associations && associations.length) await createAssociations(associations, data.id);
  }

  if (error) addError('ticket', error.message);

  return { data, error };
};

export const updateTicket = async (ticket: Ticket, forDrawer?: boolean) => {
  const { data, error } = await putTicket(ticket);

  if (data) {
    clearError();
    const subject = forDrawer ? ticketDetailSubject : ticketSubject;
    subject.next(data);
  }
  if (error) addError('ticket', error.message);
  return { data, error };
};

export const handleInfoValidation = (t: Ticket | null): ValidatorResult => {
  const result: ValidatorResult = {
    isValid: true,
    messages: [],
    invalidFields: []
  };

  if (isObserver() || isData() || !t) return result;

  if (t.status === 'Resolved' && t.resolutionType === null) {
    result.isValid = false;
    result.invalidFields.push('resolutionType');
    result.messages.push({ field: 'resolutionType', message: 'Resolution Type is required.' });
  }

  return result;
};

export const addContractReferences = async (ticketId: string) => {
  const { data, error } = await createContractReferences(ticketId);
  if (error) addError('ticket', error.message);
  if (data) clearError();
  return { data, error };
};
